import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "ghost"
    | "ghost-danger"
    | "outline"
    | "link";
  size?: "sm" | "md" | "lg";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "primary", size = "md", disabled, children, ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-[background-color,border-color,box-shadow,transform] duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-body",
          "disabled:pointer-events-none disabled:opacity-50",
          "active:translate-y-px motion-reduce:active:translate-y-0",
          {
            "bg-cta text-white hover:bg-cta-hover btn-sheen": variant === "primary",
            "bg-bg-card text-text-primary border border-border shadow-xs hover:bg-bg-hover hover:border-border-strong":
              variant === "secondary" || variant === "outline",
            "bg-danger text-white hover:bg-danger/90 btn-sheen": variant === "danger",
            "bg-transparent text-text-primary hover:bg-bg-hover":
              variant === "ghost",
            "bg-transparent text-danger hover:bg-danger-light":
              variant === "ghost-danger",
            "bg-transparent text-primary underline-offset-4 hover:underline":
              variant === "link",
          },
          {
            "h-8 px-3 text-sm": size === "sm",
            "h-10 px-4 text-sm": size === "md",
            "h-12 px-6 text-base": size === "lg",
          },
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
